import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Check, CheckCheck, Mail, MailOpen, X, AlertTriangle, Loader2 } from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";
import DataTable from "@/components/dashboard/DataTable";
import Modal from "@/components/dashboard/Modal";
import Button from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import ExpandableText from "@/components/ui/ExpandableText";
import { cn } from "@/lib/cn";
import {
  useNotifications,
  useNotificationsState,
  refresh,
  markRead,
  markUnread,
  markAllRead,
  removeMany,
  clearAll,
  openNotificationLink,
  NOTIF_LIMIT,
  NOTIF_TYPES,
  timeAgo,
  fmtDateTime,
} from "@/lib/notifications";
import { toastError } from "@/lib/toast";

const PAGE_SIZE = 8;
// Hoisted for the same reason as the Runs & Bugs tables: DataTable keeps these in
// memo dependency arrays, so a fresh literal each render re-ran the whole
// filter/sort pipeline on every keystroke.
const SEARCH_KEYS = ["title", "body", "typeLabel"];
const INITIAL_SORT = { key: "_ts", dir: "desc" };
const getRowId = (r) => r.id;

// `read` is the BOOLEAN the API serves (notifications._public → bool(n.read));
// there is no string status to map, and nothing here changes what it means.
//
// The tones come from the shared Badge so they match every other status pill in
// the product, dark mode included. Read was previously slate-on-slate — the
// house colour for "nothing to say" — so the column read as having no status at
// all. Read is now the settled/positive tone and Unread the attention tone,
// which is the distinction a reader is actually scanning for.
function ReadBadge({ read }) {
  return (
    <Badge tone={read ? "emerald" : "amber"} className="whitespace-nowrap">
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {read ? "Read" : "Unread"}
    </Badge>
  );
}

export default function Notifications() {
  // useNotifications() is what mounts the store and starts the poll; the state hook
  // only reads. The page used to read items alone, so a failed or in-flight request
  // was indistinguishable from an empty inbox and it confidently said so.
  useNotifications();
  // `unread` is the server's total. Counting the loaded rows instead greyed out
  // "Mark all read" whenever the newest NOTIF_LIMIT happened to be read, leaving
  // the older unread rows unclearable.
  const { items: notifs, unread: unreadCount, loading, error } = useNotificationsState();
  const navigate = useNavigate();

  // The store only re-renders this page when the notification LIST changes (a
  // poll that finds nothing new never calls `set()`), but `timeAgo` below reads
  // wall-clock time — so "6d ago" froze at whatever it was on the last actual
  // update and never advanced while you just sat on the page. This tick forces
  // a re-render on its own schedule so the column keeps up with real time.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  const [selected, setSelected] = useState(new Set());
  // Delete confirmation: null | { ids: string[], label: string }
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [marking, setMarking] = useState(false);

  // Shape store items into table rows (derived, display-ready fields).
  const rows = useMemo(
    () =>
      notifs.map((n) => {
        const meta = NOTIF_TYPES[n.type] || NOTIF_TYPES.info;
        return {
          ...n,
          typeLabel: meta.label,
          status: n.read ? "Read" : "Unread",
          _ts: new Date(n.createdAt).getTime() || 0,
        };
      }),
    [notifs]
  );

  const typeOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.typeLabel))),
    [rows]
  );

  // `ids: null` means "everything", not "these ids" — the store only ever holds the
  // newest 100, so deleting notifs.map(n => n.id) left the 101st and older behind
  // while the dialog promised to remove all of them.
  function askDelete(ids, label) {
    if (ids && !ids.length) return;
    setConfirm({ ids, label });
  }

  // Awaited, so `busy` genuinely spans the request and the dialog closes only when
  // the delete actually succeeded — it used to close and report success even when
  // the POST failed and the rows reappeared moments later.
  async function doDelete() {
    if (!confirm) return;
    const target = confirm;
    setBusy(true);
    try {
      if (target.ids) {
        await removeMany(target.ids);
        setSelected((prev) => {
          const next = new Set(prev);
          target.ids.forEach((id) => next.delete(id));
          return next;
        });
      } else {
        await clearAll();                 // DELETE /notifications — every row, not a page
        setSelected(new Set());
      }
      setConfirm(null);
    } catch (e) {
      /* the store toasts and re-syncs; leave the dialog open so it can be retried */
    } finally {
      setBusy(false);
    }
  }

  const selectedIds = [...selected];
  // Only the genuinely unread rows need a request — "Mark read" after select-all
  // used to POST once per selected row, most of them already read.
  const unreadSelectedIds = notifs.filter((n) => selected.has(n.id) && !n.read).map((n) => n.id);

  // Awaited, and its failures reported. This was `forEach(markRead)` — N
  // unawaited requests with no busy state and no error surfacing, so half of
  // forty failing looked exactly like all forty succeeding.
  async function markSelectedRead() {
    if (marking || unreadSelectedIds.length === 0) return;
    setMarking(true);
    try {
      const results = await Promise.allSettled(unreadSelectedIds.map((id) => markRead(id)));
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed) {
        toastError(`${failed} of ${results.length} notifications couldn't be marked read.`);
      } else {
        setSelected(new Set());
      }
    } finally {
      setMarking(false);
    }
  }

  const columns = [
    {
      key: "typeLabel",
      header: "Type",
      sortable: true,
      render: (r) => {
        const meta = NOTIF_TYPES[r.type] || NOTIF_TYPES.info;
        const Icon = meta.icon;
        return (
          <span className="inline-flex items-center gap-2">
            <span className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-lg", meta.tone)}>
              <Icon size={16} />
            </span>
            {/* Below `sm` the coloured icon carries the type on its own and the
                word is what a narrow screen can least afford — this is the only
                thing hidden at small widths, and nothing unique is lost. */}
            <span className="hidden text-xs font-medium text-slate-600 dark:text-slate-300 sm:inline">
              {meta.label}
            </span>
          </span>
        );
      },
    },
    {
      key: "title",
      header: "Notification",
      sortable: true,
      // A BOUNDED, WRAPPING column — this is what stops a long notification
      // widening the whole table.
      //
      // It used to be `min-w-[16rem]` with `truncate` on both lines. `truncate`
      // includes `white-space: nowrap`, and the table is `table-layout: auto`,
      // which sizes a column to its content's UNWRAPPED width — so one long
      // notification made this column 2,526px, the table 2,999px inside a
      // 1,086px card, and pushed the Actions column off the right-hand edge.
      // Nothing was hidden; the column simply grew. A max-width plus text that
      // is allowed to wrap (and to break an unbroken URL) keeps the content
      // measurable, so the table stays inside its container.
      // The floor is lifted only where there is room for it: on a phone a 14rem
      // minimum is width this column does not have to spare, and DataTable
      // already sets a 640px floor for the table as a whole.
      className: "max-w-[22rem] sm:min-w-[14rem] lg:max-w-[30rem]",
      render: (r) => (
        <div className="min-w-0 max-w-full">
          <p className={cn(
            "line-clamp-2 break-words [overflow-wrap:anywhere] text-sm",
            r.read ? "font-medium text-slate-700 dark:text-slate-300"
                   : "font-semibold text-slate-900 dark:text-white")}>
            {r.title}
          </p>
          {r.body && (
            // ExpandableText is the product's existing answer to "author text of
            // unknown length in a fixed box": a clamped preview, a Read more that
            // shows the rest in place, and the min-w-0 / overflow-wrap rules that
            // keep an unbroken 240-character token from inflating the column.
            // Nothing is truncated away — the full text is one click down.
            //
            // The click is stopped here so opening a long notification to READ it
            // is not also a navigation; the title above still opens the row.
            <div onClick={(e) => e.stopPropagation()}>
              <ExpandableText
                text={r.body}
                previewLines={2}
                expandable
                className="mt-0.5 text-xs text-slate-500 dark:text-slate-400"
              />
            </div>
          )}
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (r) => <ReadBadge read={r.read} />,
    },
    {
      key: "_ts",
      header: "Received",
      sortable: true,
      // The STORED timestamp, formatted by the shared lib/dates.formatDateTime
      // (re-exported as fmtDateTime) — the same dd/mm/yyyy HH:mm every other
      // date in the product uses, in the viewer's own timezone, from the UTC
      // instant the API serves. Nothing is computed from the current time.
      //
      // "5d ago" alone was all this column showed, which never answers "when did
      // this actually arrive?". The relative line is kept underneath because it
      // is genuinely the faster read for recent rows, but it is no longer the
      // only thing on offer.
      render: (r) => {
        // Past four weeks timeAgo() gives up and returns a plain date, which
        // would print the same date twice. Only show the second line while it
        // is actually saying something the first line doesn't.
        const ago = timeAgo(r.createdAt);
        const relative = /ago$|^just now$/.test(ago) ? ago : "";
        return (
          <div className="whitespace-nowrap text-xs leading-tight">
            <div className="font-medium text-slate-600 dark:text-slate-300">
              {fmtDateTime(r.createdAt)}
            </div>
            {relative && <div className="text-slate-400">{relative}</div>}
          </div>
        );
      },
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            title={r.read ? "Mark as unread" : "Mark as read"}
            onClick={(e) => { e.stopPropagation(); r.read ? markUnread(r.id) : markRead(r.id); }}
            className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-brand-600 dark:hover:bg-white/10 dark:hover:text-brand-300"
          >
            {r.read ? <Mail size={15} /> : <MailOpen size={15} />}
          </button>
          <button
            type="button"
            title="Delete"
            onClick={(e) => { e.stopPropagation(); askDelete([r.id], r.title); }}
            className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-500 dark:hover:bg-rose-500/10"
          >
            <Trash2 size={15} />
          </button>
        </div>
      ),
    },
  ];

  const bulkBar =
    selected.size > 0 ? (
      <div className="flex flex-col gap-2 rounded-xl border border-brand-200 bg-brand-50/70 px-3 py-2.5 dark:border-brand-500/25 dark:bg-brand-500/10 sm:flex-row sm:items-center sm:justify-between">
        <span className="text-sm font-semibold text-brand-700 dark:text-brand-200">
          {selected.size} selected
        </span>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={marking || unreadSelectedIds.length === 0}
            onClick={markSelectedRead}
          >
            {marking ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Mark read
          </Button>
          <Button size="sm" variant="danger" onClick={() => askDelete(selectedIds, `${selected.size} notification${selected.size > 1 ? "s" : ""}`)}>
            <Trash2 size={15} /> Delete selected
          </Button>
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            title="Clear selection"
            className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-white hover:text-slate-700 dark:hover:bg-white/10 dark:hover:text-white"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    ) : null;

  return (
    <>
      <PageHeader
        title="Notifications"
        subtitle={
          // Don't reassure while we don't actually know.
          error ? "Couldn't load your notifications."
            : loading && notifs.length === 0 ? "Loading…"
            : unreadCount > 0
              ? `You have ${unreadCount} unread notification${unreadCount > 1 ? "s" : ""}.`
              : "You're all caught up."
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={markAllRead} disabled={unreadCount === 0}>
              <CheckCheck size={15} /> Mark all read
            </Button>
            {/* cn() is a plain string joiner, and Tailwind emits colour utilities
                alphabetically — so `text-rose-600` layered on the outline variant
                lost to its own `text-slate-700` and this rendered grey, not red.
                Override the token the variant sets, with `!`. */}
            <Button
              size="sm"
              variant="outline"
              className="!text-rose-600 hover:bg-rose-50 dark:!text-rose-300 dark:hover:bg-rose-500/10"
              onClick={() => askDelete(null, "all notifications")}
              disabled={notifs.length === 0}
            >
              <Trash2 size={15} /> Clear all
            </Button>
          </div>
        }
      />

      {/* With nothing loaded the table is hidden so it can't print an empty-inbox
          message over the spinner / error banner below; rows we already have stay
          visible even if a later poll fails. DataTable substitutes its own message
          when a search or filter is what narrowed the rows away, so `emptyLabel`
          is only ever shown for a genuinely empty inbox. */}
      {(notifs.length > 0 || (!loading && !error)) && (
        <DataTable
          columns={columns}
          rows={rows}
          getRowId={getRowId}
          searchKeys={SEARCH_KEYS}
          filters={[
            { key: "status", label: "status", options: ["Unread", "Read"] },
            { key: "typeLabel", label: "types", options: typeOptions },
          ]}
          initialSort={INITIAL_SORT}
          pageSize={PAGE_SIZE}
          maxHeight="42rem"
          emptyLabel="You have no notifications."
          selectable
          selectedIds={selected}
          onSelectionChange={setSelected}
          onRowClick={(r) => {
            if (!r.read) markRead(r.id);
            openNotificationLink(r.link, navigate);
          }}
          toolbarExtra={bulkBar}
        />
      )}

      {/* The list endpoint has no offset, so anything past the cap is unreachable
          from here. Say so rather than letting the footer imply that's all of it. */}
      {notifs.length >= NOTIF_LIMIT && (
        <p className="mt-2 text-xs text-slate-400">
          Showing the most recent {NOTIF_LIMIT} notifications.
        </p>
      )}

      {/* Previously a failed request also rendered "You have no notifications." —
          the one case where saying the inbox is empty is actively misleading. */}
      {error ? (
        <div className="mt-4 flex flex-wrap items-center gap-2 rounded-2xl border border-rose-200 bg-rose-50/70 px-4 py-3 text-sm text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200">
          <AlertTriangle size={15} className="shrink-0" />
          <span className="flex-1">Couldn&apos;t load your notifications.</span>
          <button type="button" onClick={refresh}
            className="rounded-lg border border-rose-300 px-2.5 py-1 text-xs font-medium hover:bg-rose-100 dark:border-rose-500/30 dark:hover:bg-rose-500/20">
            Retry
          </button>
        </div>
      ) : loading && notifs.length === 0 ? (
        <div className="mt-4 flex justify-center py-10 text-slate-400">
          <Loader2 size={22} className="animate-spin" />
        </div>
      ) : null}

      <Modal
        open={!!confirm}
        onClose={() => (busy ? null : setConfirm(null))}
        title="Delete notifications?"
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
            <Button size="sm" variant="danger" onClick={doDelete} disabled={busy}>
              {busy ? <><Loader2 size={15} className="animate-spin" /> Deleting…</> : <><Trash2 size={15} /> Delete</>}
            </Button>
          </div>
        }
      >
        <div className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50/70 p-4 text-sm text-rose-800 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <p>
            This permanently removes <strong>{confirm?.label}</strong>. This can&apos;t be undone.
          </p>
        </div>
      </Modal>
    </>
  );
}
